import { ExclamationIcon } from "@heroicons/react/solid";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import cx from "classnames";
import React, { useEffect, useState } from "react";
import { InputGroupField } from "components/common/Form";
import { sanitizeWordpressContent } from "helpers/strings";
import { CourseQuizQuestion } from "../types";
import styles from "./SingleChoiceQuestion.module.css";

const removeQuestionIndex = (question: string) => {
  return question.replace(/^\d+\.\s+/, "");
};

interface SingleChoiceQuestionProps {
  question: CourseQuizQuestion;
  hasSubmitted: boolean;
  hasPassed: boolean;
}

const SingleChoiceQuestion = ({ question, hasSubmitted, hasPassed }: SingleChoiceQuestionProps) => {
  const { answers, isIncorrect } = question;
  const [selectedAnswer, setSelectedAnswer] = useState<number | false>(() =>
    question.answers.findIndex(a => a?.isMyAnswer)
  );
  const [incorrectHighlight, setIncorrectHighlight] = useState(false);

  if (!question.question) throw new Error(`Invalid question: ${JSON.stringify(question)}`);

  const alpha = "abcdefghijklmnopqrstuvwxyz";
  const questionText = sanitizeWordpressContent(removeQuestionIndex(question.question));

  useEffect(() => {
    if (hasSubmitted && isIncorrect) {
      setSelectedAnswer(false);
      setIncorrectHighlight(true);
    }
  }, [hasSubmitted, isIncorrect]);

  const handleAnswerChange = (i: number) => {
    setSelectedAnswer(i);
    setIncorrectHighlight(false);
  };

  const showIncorrectHighlight = hasSubmitted && isIncorrect && incorrectHighlight && !hasPassed;

  return (
    <>
      <div
        className={cx(styles.question, {
          [styles.incorrect]: showIncorrectHighlight,
        })}
      >
        <p // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: questionText }}
        />

        {showIncorrectHighlight && (
          <div className={styles.incorrectIconContainer}>
            <ExclamationIcon className={styles.incorrectIcon} aria-hidden="true" /> Incorrect,
            please try again
          </div>
        )}
      </div>

      <ol className={styles.answers}>
        {answers.filter(notEmpty).map((answer, i) => {
          if (!question.id || !answer.title || !answer.id) return null;

          const isSelected = i === selectedAnswer;

          return (
            <li key={answer.id}>
              <label htmlFor={answer.id} className={styles.outerLabel}>
                <div>{alpha[i]}.</div>
                <div
                  className={cx(styles.answer, {
                    [styles.selected]: !hasPassed && isSelected,
                    [styles.correct]: hasPassed && answer.isCorrect,
                    [styles.incorrect]: hasPassed && answer.isMyAnswer && !answer.isCorrect,
                  })}
                >
                  <InputGroupField
                    key={answer.id}
                    type="radio"
                    name={question.id}
                    label={answer.title}
                    value={i.toString()}
                    checked={isSelected}
                    onClick={() => handleAnswerChange(i)}
                    id={answer.id}
                  />
                </div>
              </label>
            </li>
          );
        })}
      </ol>
    </>
  );
};

export default SingleChoiceQuestion;
